import { useEffect, useLayoutEffect, useState } from "react";
import fallbackHeroImage from "../../../src/content/assets/unsplash/us-hero-landscape.jpg";
import { fetchUs, type ApiUs } from "./data/api";
import "./Us.css";

const DAY_MS = 24 * 60 * 60 * 1000;
const TODAY_FORMAT = new Intl.DateTimeFormat("en", {
	timeZone: "Asia/Shanghai", year: "numeric", month: "2-digit", day: "2-digit",
});
const DATE_FORMAT = new Intl.DateTimeFormat("zh-CN", {
	timeZone: "UTC", year: "numeric", month: "long", day: "numeric",
});

function getRelationshipStats(now: Date, startDateValue: string) {
	const startDate = new Date(`${startDateValue}T00:00:00Z`);
	const parts = TODAY_FORMAT.formatToParts(now);
	const year = Number(parts.find((part) => part.type === "year")!.value);
	const month = Number(parts.find((part) => part.type === "month")!.value) - 1;
	const day = Number(parts.find((part) => part.type === "day")!.value);
	const today = new Date(Date.UTC(year, month, day));
	let months = (year - startDate.getUTCFullYear()) * 12 + month - startDate.getUTCMonth();
	let anchor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + months, startDate.getUTCDate()));
	if (anchor > today) {
		months -= 1;
		anchor = new Date(Date.UTC(startDate.getUTCFullYear(), startDate.getUTCMonth() + months, startDate.getUTCDate()));
	}
	const anniversaryYear = Math.max(year, startDate.getUTCFullYear() + 1);
	let anniversary = new Date(Date.UTC(anniversaryYear, startDate.getUTCMonth(), startDate.getUTCDate()));
	if (anniversary < today) anniversary = new Date(Date.UTC(anniversaryYear + 1, startDate.getUTCMonth(), startDate.getUTCDate()));
	return {
		today,
		elapsedDays: (today.getTime() - startDate.getTime()) / DAY_MS + 1,
		months,
		days: (today.getTime() - anchor.getTime()) / DAY_MS,
		anniversary,
		anniversaryNumber: anniversary.getUTCFullYear() - startDate.getUTCFullYear(),
		untilAnniversary: (anniversary.getTime() - today.getTime()) / DAY_MS,
	};
}

export default function UsPage() {
	const [content, setContent] = useState<ApiUs | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [now, setNow] = useState(() => new Date());

	useEffect(() => {
		fetchUs().then(setContent).catch((reason: unknown) => setError(reason instanceof Error ? reason.message : "无法读取 Us 页面数据"));
	}, []);

	useLayoutEffect(() => {
		window.scrollTo({ top: 0, behavior: "instant" });
	}, []);

	useEffect(() => {
		const previousTitle = document.title;
		document.title = "Us · CQBLOG JOURNAL";
		const refresh = () => setNow(new Date());
		const interval = window.setInterval(refresh, 60_000);
		document.addEventListener("visibilitychange", refresh);
		return () => {
			document.title = previousTitle;
			window.clearInterval(interval);
			document.removeEventListener("visibilitychange", refresh);
		};
	}, []);

	if (error) return <main className="data-state"><p>{error}</p></main>;
	if (!content) return <main className="data-state"><p>正在打开 Us 页面…</p></main>;

	const stats = getRelationshipStats(now, content.startDate);
	const startDate = new Date(`${content.startDate}T00:00:00Z`);
	const todayValue = stats.today.toISOString().slice(0, 10);
	const anniversaryValue = stats.anniversary.toISOString().slice(0, 10);
	const heroImage = content.heroImage ?? fallbackHeroImage;

	return <main className="us-page">
		<section className="us-cover" aria-label={`${content.partnerA}和${content.partnerB}的纪念日`}>
			<div className="us-journal">
				<header className="us-heading">
					<p className="us-kicker">OUR JOURNAL</p>
					<h1>{content.partnerA} <span>×</span> {content.partnerB}</h1>
					<p className="us-heading-note">{content.headingNote}</p>
				</header>
				<div className="us-spread">
					<figure className="us-photo">
						<img src={heroImage} alt="落日下的湖面与远山风景" width={1800} height={1200} decoding="async" />
						<figcaption><span>开始于 <time dateTime={content.startDate}>{DATE_FORMAT.format(startDate)}</time></span><span>01 / OUR START</span></figcaption>
					</figure>
					<section className="us-today" aria-labelledby="us-today-heading">
						<h2 id="us-today-heading">今天，我们在一起的</h2>
						<p className="us-day-count">第 <strong>{stats.elapsedDays}</strong> 天</p>
						<p className="us-copy">把属于{content.partnerA}和{content.partnerB}的时间，<br />认真记下来。</p>
						<dl className="us-milestones">
							<div>
								<dt>已经一起走过</dt>
								<dd>{stats.months}<span> 个月 </span>{stats.days}<span> 天</span></dd>
							</div>
							<div>
								<dt>距离第 {stats.anniversaryNumber} 个周年</dt>
								<dd>{stats.untilAnniversary === 0 ? "就是今天" : <><span>还有 </span>{stats.untilAnniversary}<span> 天</span></>}</dd>
								<dd className="us-next-date">下一次纪念日 <time dateTime={anniversaryValue}>{DATE_FORMAT.format(stats.anniversary)}</time></dd>
							</div>
						</dl>
					</section>
				</div>
			</div>
			<footer className="us-footer">
				<span>OUR START <time dateTime={content.startDate}>{content.startDate.replaceAll("-", ".")}</time></span>
				<a href="#our-story">Our story ↓</a>
				<span>TODAY <time dateTime={todayValue}>{todayValue.replaceAll("-", ".")}</time></span>
			</footer>
		</section>
		<section className="us-chapter" id="our-story" aria-labelledby="us-story-heading">
			<header className="us-chapter-heading">
				<div><p className="us-kicker">01 / OUR STORY</p><h2 id="us-story-heading">重要时刻</h2></div>
				<p className="us-chapter-count">{String(content.moments.length).padStart(2, "0")} / {content.moments.length === 1 ? "MOMENT" : "MOMENTS"}</p>
			</header>
			{content.moments.length ? <ol className="us-story-list">
				{content.moments.map((moment) => <li key={moment.id}>
					<time dateTime={moment.date}>{moment.date.replaceAll("-", ".")}</time>
					<div className="us-event-details"><h3>{moment.title}</h3><p>{moment.note}</p></div>
				</li>)}
			</ol> : <div className="us-photos-empty"><p className="us-kicker">NO MOMENTS YET</p><p>还没有记录重要时刻。</p></div>}
		</section>
		<section className="us-chapter us-photographs" aria-labelledby="us-photographs-heading">
			<header className="us-chapter-heading">
				<div><p className="us-kicker">02 / PHOTOGRAPHS</p><h2 id="us-photographs-heading">照片与注记</h2></div>
				<p className="us-chapter-count">{String(content.photographs.length).padStart(2, "0")} / PHOTOGRAPHS</p>
			</header>
			{content.photographs.length > 0 ? <div className="us-photo-gallery">
				{content.photographs.map((photo) => <figure key={photo.id}>
					<img src={photo.image} alt={photo.alt} loading="lazy" decoding="async" />
					<figcaption><time dateTime={photo.date}>{photo.date.replaceAll("-", ".")}</time><p>{photo.note}</p></figcaption>
				</figure>)}
			</div> : <div className="us-photos-empty">
				<p className="us-kicker">NO PHOTOGRAPHS YET</p>
				<p>还没有放入照片。</p>
				<span>留一点空白，给接下来的故事。</span>
			</div>}
		</section>
	</main>;
}
